/// <reference types="vite/client" />
import { useState, useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

import { DetectionResult, StreamConfig, StreamStatus } from '../types';

export const useBirdSocket = () => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [detections, setDetections] = useState<DetectionResult[]>([]);
  const [streamStatus, setStreamStatus] = useState<StreamStatus>({ status: 'disconnected' });
  
  const socketRef = useRef<Socket | null>(null);
  
  useEffect(() => {
    // Use environment variable for socket URL
    const socketUrl = import.meta.env.VITE_SOCKET_URL || 'http://localhost:5001';
    
    // For production with subpath, extract base URL and path separately
    let baseUrl = socketUrl;
    let socketPath = '/socket.io/';
    
    if (socketUrl.includes('/birdsong-api')) {
      baseUrl = socketUrl.replace('/birdsong-api', '');
      socketPath = '/birdsong-api/socket.io/';
    }
    
    const newSocket = io(baseUrl, {
      transports: ['websocket'],
      path: socketPath
    });
    
    newSocket.on('connect', () => {
      setIsConnected(true);
      setStreamStatus({ status: 'connected', message: 'Connected to BirdNET server' });
    });
    
    newSocket.on('disconnect', () => {
      setIsConnected(false);
      setStreamStatus({ status: 'disconnected', message: 'Disconnected from server' });
    });
    
    newSocket.on('detection_result', (data: DetectionResult) => {
      try {
        // Validate detection data structure
        if (data && typeof data === 'object' && Array.isArray(data.detections)) {
          setDetections(prev => [data, ...prev].slice(0, 50)); // Keep last 50 detections
        } else {
          console.warn('Invalid detection result received:', data);
        }
      } catch (error) {
        console.error('Error processing detection result:', error);
      }
    });
    
    newSocket.on('stream_status', (status: StreamStatus) => {
      try {
        if (status && typeof status === 'object' && status.status) {
          setStreamStatus(status);
        } else {
          console.warn('Invalid stream status received:', status);
        }
      } catch (error) {
        console.error('Error processing stream status:', error);
      }
    });
    
    socketRef.current = newSocket;
    setSocket(newSocket);
    
    return () => {
      newSocket.close();
    };
  }, []);
  
  const startDetection = useCallback((config: StreamConfig) => {
    if (socketRef.current) {
      socketRef.current.emit('start_stream', config);
      setDetections([]); // Clear previous detections
    }
  }, []);
  
  const stopDetection = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.emit('stop_stream');
    }
  }, []);
  
  const sendAudioChunk = useCallback((audioData: Float32Array) => {
    if (socketRef.current && isConnected) {
      // Convert Float32Array to bytes for transmission (matching working HTML version)
      const buffer = new ArrayBuffer(audioData.length * 4);
      const view = new Float32Array(buffer);
      view.set(audioData);
      
      // Convert to base64 and send (exactly like working webaudio_test.html)
      const uint8Array = new Uint8Array(buffer);
      try {
        // Use safe approach to avoid stack overflow with large arrays
        let binaryString = '';
        const chunkSize = 8192;
        for (let i = 0; i < uint8Array.length; i += chunkSize) {
          const chunk = uint8Array.slice(i, i + chunkSize);
          binaryString += String.fromCharCode(...chunk);
        }
        const base64Data = btoa(binaryString);
      
        socketRef.current.emit('audio_chunk', {
          audio: base64Data,
          timestamp: Date.now(),
          format: 'pcm_float32',
          sample_rate: 48000,
          channels: 1,
          samples: audioData.length
        });
      } catch (error) {
        console.error('Error encoding audio data:', error);
      }
    }
  }, [isConnected]);
  
  return {
    socket,
    isConnected,
    detections,
    streamStatus,
    startDetection,
    stopDetection,
    sendAudioChunk
  };
};